import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, Plus, FileText, Upload, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const FORM_TYPES = [
  { value: 'sale_auth_keeneland', label: 'Sale Authorization - Keeneland' },
  { value: 'sale_auth_fasig', label: 'Sale Authorization - Fasig-Tipton' },
  { value: 'sale_auth_obs', label: 'Sale Authorization - OBS' },
  { value: 'sale_auth_inglis', label: 'Sale Authorization - Inglis' },
  { value: 'questionnaire_weanling', label: 'Questionnaire - Weanling' },
  { value: 'questionnaire_yearling', label: 'Questionnaire - Yearling' },
  { value: 'questionnaire_broodmare', label: 'Questionnaire - Broodmare / Broodmare Prospect' },
  { value: 'questionnaire_racing_prospect', label: 'Questionnaire - Racing Prospect' },
];

const FormDocumentsTab = () => {
  const [forms, setForms] = useState([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    form_type: '',
    version: '1.0',
    file: null
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await api.get('/form-documents');
      setForms(response.data || []);
    } catch (error) {
      console.error('Error fetching forms:', error);
      setForms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  const handleUpload = async () => {
    if (!formData.form_type || !formData.file) {
      toast.error('Please select form type and file');
      return;
    }

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', formData.file);
      formDataObj.append('form_type', formData.form_type);
      formDataObj.append('version', formData.version);

      await api.post('/form-documents', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Form document uploaded');
      setDialogOpen(false);
      setFormData({ form_type: '', version: '1.0', file: null });
      fetchForms();
    } catch (error) {
      console.error('Error uploading form:', error);
      toast.error('Failed to upload form document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (formId) => {
    if (!await confirm({ title: 'Delete this form document?', destructive: true })) return;

    try {
      await api.delete(`/form-documents/${formId}`);
      toast.success('Form deleted');
      fetchForms();
    } catch (error) {
      console.error('Error deleting form:', error);
      toast.error('Failed to delete form');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Form Documents</CardTitle>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Upload Form
          </Button>
        </CardHeader>
        <CardContent>
          {forms.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No form documents uploaded</p>
              <Button onClick={() => setDialogOpen(true)} variant="outline" className="mt-4">
                <Upload className="w-4 h-4 mr-2" /> Upload First Form
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {forms.map(form => (
                <div key={form.id} className="flex items-center justify-between p-4 border rounded hover:bg-muted/30">
                  <div className="flex-1">
                    <p className="font-medium">
                      {FORM_TYPES.find(t => t.value === form.form_type)?.label || form.form_type}
                    </p>
                    <p className="text-xs text-muted-foreground">Version: {form.version}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm">
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(form.id)} className="text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Form Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Form Type</Label>
              <Select value={formData.form_type} onValueChange={(value) => setFormData({ ...formData, form_type: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select form type..." />
                </SelectTrigger>
                <SelectContent>
                  {FORM_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Version</Label>
              <Input
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="1.0"
              />
            </div>

            <div className="space-y-2">
              <Label>PDF File</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={formData.file?.name || ''}
                  placeholder="No file selected..."
                  disabled
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('form-file-input').click()}
                >
                  Browse...
                </Button>
              </div>
              <input
                id="form-file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormDocumentsTab;
